import React from 'react';
import styled from 'styled-components';
import { connect, shallowEqual } from 'react-redux';
import axios from 'axios';
import { useNavigate, useMatches } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import './index.scss';

import { useAppSelector, useAppDispatch } from '../../../store';
import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';
import { loadedWheelStatus } from '../../../modules/wheel/store/actions/wheel';
import { fetchWheelsDataSource } from '@/store/slices/wheelsDataSource';
import { cloneDeep } from 'lodash-es';

type WheelProps = {
  children: any;
  styleText: string;
  className: string;
  loadedWheelStatus(status: any, skip?: any): void;
  wheelSetups: any;
  authentication: any;
  properties?: {
    dsType: string;
  };
};

const apiUrl = window.config.betsApiUrl + '/wheel/player';

const defaultProps = {
  className: '',
  styleText: '',
  properties: {
    dsType: '',
  },
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const WheelsModule = (componentProps: WheelProps) => {
  let props = componentProps;

  const dataElementContext = React.useContext(DataElementContext);
  [props] = processComponentProps(props, dataElementContext);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { authentication, wheelsLoaded, wheelSetups } = useAppSelector<{
    authentication: any;
    wheelsLoaded: boolean;
    wheelSetups: any;
  }>(
    (state) => ({
      wheelSetups: state.wheelsDataSource.wheels,
      wheelsLoaded: state.wheelsDataSource.loaded,
      authentication: state.authentication,
    }),
    shallowEqual,
  );
  const [statuses, setStatuses] = React.useState<any[]>([]);
  const [index, setIndex] = React.useState(0);
  const [refresh, setRefresh] = React.useState(0);

  const onTabClick = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const tab = e.currentTarget?.dataset?.tab;
      if (tab) {
        const tabNo = parseInt(tab, 10);
        if (!isNaN(tabNo) && tabNo >= 0 && tabNo < statuses.length) {
          setIndex(tabNo);
        }
      }
    },
    [statuses],
  );

  React.useEffect(() => {
    if (!wheelsLoaded) {
      dispatch(fetchWheelsDataSource({ id: window.config.wheelSetupsDataSource }));
    }
  }, [wheelsLoaded, dispatch]);

  React.useEffect(() => {
    if (['user', 'token'].indexOf(authentication.auth_type) === -1 || authentication.access_token === null) {
      return;
    }
    axios
      .get(apiUrl + '/status/all', {
        headers: {
          Authorization: 'Bearer ' + authentication.access_token,
        },
      })
      .then((response) => {
        setStatuses(response.data);
      })
      .catch((err) => {});
  }, [authentication, refresh]);

  const canFreeSpin = React.useCallback((status: any) => {
    if (status && status.free_spin && status.free_spin.status && status.free_spin.status === 'available') {
      return true;
    }
    return false;
  }, []);
  const canKeysSpin = React.useCallback((status: any) => {
    if (status && status.keys_spin && status.keys_spin.status && status.keys_spin.status === 'available') {
      return true;
    }
    return false;
  }, []);
  const canNextSpin = React.useCallback((status: any) => {
    if (status && status.free_spin && status.free_spin.next_spin_after) {
      return true;
    }
    return false;
  }, []);
  const onRefresh = React.useCallback((status: any) => {
    setRefresh((prev) => prev + 1);
  }, []);

  const contextValue: any = React.useMemo(() => {
    const setups = cloneDeep(statuses).map((status: any) => {
      const wheelSetup = wheelSetups[status.wheel_setup] ?? null;
      if (wheelSetup) {
        return {
          ...status,
          wheel_setup_data: wheelSetup,
          can_free_spin: canFreeSpin(status),
          can_keys_spin: canKeysSpin(status),
          can_next_spin: canNextSpin(status),
        };
      }
    });

    return {
      setups,
      tabIndex: index,
      actions: {
        onTabClick,
        onRefresh: onRefresh,
      },
    };
  }, [wheelSetups, statuses, index, onTabClick, onRefresh, canFreeSpin, canKeysSpin, canNextSpin]);

  console.log('Wheels[contextValue]: ', contextValue);

  return (
    <ModuleElementDiv className={props.className ?? ''} $styleText={props.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

const mapStateToProps = (state: any) => {
  return {
    wheelSetups: state.config.wheelSetups,
    authentication: state.authentication,
  };
};

const mapActions = {
  loadedWheelStatus: loadedWheelStatus,
};

const Wheels = connect(mapStateToProps, mapActions)(WheelsModule);

export default Wheels;
